/**
 * Gaushala Offline Database Schema
 * Uses Dexie.js for IndexedDB management
 *
 * This database stores:
 * 1. Pending operations (CREATE/UPDATE/DELETE) for sync when online
 * 2. Cached entities for offline reading
 * 3. Master data cache for dropdowns
 * 4. Sync metadata for tracking last sync times
 */

import Dexie, { Table } from 'dexie';
import {
  EntityType,
  OperationType,
  SyncStatus,
  CacheStatus,
  MasterDataKey
} from './constants';

// ============== PENDING OPERATION ==============
/**
 * Represents an operation waiting to be synced to the server
 */
export interface PendingOperation {
  id: string;                    // UUID
  operationType: OperationType;  // CREATE, UPDATE, DELETE
  entityType: EntityType;        // cattle, healthRecord, etc.
  data: any;                     // The payload to send to server
  tempId?: string;               // For CREATEs - local temporary ID
  serverId?: number;             // For UPDATEs/DELETEs - server ID
  gaushalaId: number;            // Gaushala context
  createdAt: Date;               // When operation was queued
  retryCount: number;            // Number of sync attempts
  status: SyncStatus;            // pending, syncing, failed, completed
  errorMessage?: string;         // Error message if failed
  lastAttempt?: Date;            // Last sync attempt timestamp
}

// ============== CACHED ENTITY ==============
/**
 * Generic cached entity wrapper
 */
export interface CachedEntity<T = any> {
  id: number | string;           // Server ID or temp ID (string for temp)
  isTemp: boolean;               // True if not yet synced to server
  syncStatus: CacheStatus;       // synced, pending, conflict
  data: T;                       // The actual entity data
  cachedAt: Date;                // When cached
  gaushalaId: number;            // Gaushala context
  version?: number;              // Optimistic locking version
}

// ============== MASTER DATA CACHE ==============
/**
 * Cached master data (breeds, species, etc.)
 */
export interface CachedMasterData {
  key: MasterDataKey;            // breeds, species, etc.
  data: any[];                   // Array of master data items
  cachedAt: Date;                // When cached
  expiresAt: Date;               // When cache expires
}

// ============== SYNC METADATA ==============
/**
 * Tracks sync state per entity type per gaushala
 */
export interface SyncMetadata {
  id?: number;                   // Auto-increment
  gaushalaId: number;            // Gaushala context
  entityType: EntityType;        // Entity being tracked
  lastSyncAt: Date;              // Last successful sync
  lastServerVersion?: number;    // Server version for delta sync
  recordCount?: number;          // Number of records synced
}

// ============== DATABASE CLASS ==============
/**
 * Dexie database class for Gaushala offline storage
 */
class GaushalaOfflineDB extends Dexie {
  // Operations queue
  pendingOperations!: Table<PendingOperation, string>;

  // Entity caches (one table per entity type for better indexing)
  cachedCattle!: Table<CachedEntity, number | string>;
  cachedHealthRecords!: Table<CachedEntity, number | string>;
  cachedMilkProduction!: Table<CachedEntity, number | string>;
  cachedFoodHistory!: Table<CachedEntity, number | string>;
  cachedMedicine!: Table<CachedEntity, number | string>;
  cachedInventory!: Table<CachedEntity, number | string>;
  cachedSheds!: Table<CachedEntity, number | string>;
  cachedDungTransactions!: Table<CachedEntity, number | string>;
  cachedRfidScans!: Table<CachedEntity, number | string>;

  // Master data & metadata
  cachedMasterData!: Table<CachedMasterData, string>;
  syncMetadata!: Table<SyncMetadata, number>;

  constructor() {
    super('GaushalaOfflineDB');

    // Define schema version 1
    this.version(1).stores({
      // Pending operations - indexed by id, status, entityType, gaushalaId, createdAt
      pendingOperations: 'id, status, entityType, gaushalaId, createdAt, [status+entityType]',

      // Cached entities - indexed by id, isTemp, syncStatus, gaushalaId
      // Compound index [gaushalaId+syncStatus] for filtering pending items per gaushala
      cachedCattle: 'id, isTemp, syncStatus, gaushalaId, cachedAt, [gaushalaId+syncStatus]',
      cachedHealthRecords: 'id, isTemp, syncStatus, gaushalaId, cachedAt, [gaushalaId+syncStatus], [data.cattleId]',
      cachedMilkProduction: 'id, isTemp, syncStatus, gaushalaId, cachedAt, [gaushalaId+syncStatus]',
      cachedFoodHistory: 'id, isTemp, syncStatus, gaushalaId, cachedAt, [gaushalaId+syncStatus]',
      cachedMedicine: 'id, isTemp, syncStatus, gaushalaId, cachedAt, [gaushalaId+syncStatus]',
      cachedInventory: 'id, isTemp, syncStatus, gaushalaId, cachedAt, [gaushalaId+syncStatus]',
      cachedSheds: 'id, isTemp, syncStatus, gaushalaId, cachedAt, [gaushalaId+syncStatus]',
      cachedDungTransactions: 'id, isTemp, syncStatus, gaushalaId, cachedAt, [gaushalaId+syncStatus]',
      cachedRfidScans: 'id, isTemp, syncStatus, gaushalaId, cachedAt, [gaushalaId+syncStatus]',

      // Master data - indexed by key
      cachedMasterData: 'key, cachedAt',

      // Sync metadata - compound primary key [gaushalaId+entityType]
      syncMetadata: '++id, [gaushalaId+entityType], entityType, lastSyncAt',
    });
  }

  // ============== HELPER METHODS ==============

  /**
   * Get the cache table for a given entity type
   */
  getCacheTable(entityType: EntityType): Table<CachedEntity, number | string> {
    const tableMap: Record<EntityType, Table<CachedEntity, number | string>> = {
      cattle: this.cachedCattle,
      healthRecord: this.cachedHealthRecords,
      milkProduction: this.cachedMilkProduction,
      foodHistory: this.cachedFoodHistory,
      medicine: this.cachedMedicine,
      inventory: this.cachedInventory,
      stockTransaction: this.cachedInventory, // Shares with inventory
      shed: this.cachedSheds,
      dungTransaction: this.cachedDungTransactions,
      rfidScan: this.cachedRfidScans,
    };
    return tableMap[entityType];
  }

  /**
   * Clear all cached data (useful for logout)
   */
  async clearAllCache(): Promise<void> {
    await Promise.all([
      this.cachedCattle.clear(),
      this.cachedHealthRecords.clear(),
      this.cachedMilkProduction.clear(),
      this.cachedFoodHistory.clear(),
      this.cachedMedicine.clear(),
      this.cachedInventory.clear(),
      this.cachedSheds.clear(),
      this.cachedDungTransactions.clear(),
      this.cachedRfidScans.clear(),
      this.cachedMasterData.clear(),
      this.syncMetadata.clear(),
    ]);
    console.log('[OfflineDB] All cache cleared');
  }

  /**
   * Clear pending operations (use with caution)
   */
  async clearPendingOperations(): Promise<void> {
    await this.pendingOperations.clear();
    console.log('[OfflineDB] Pending operations cleared');
  }

  /**
   * Get count of pending operations
   */
  async getPendingCount(): Promise<number> {
    return await this.pendingOperations
      .where('status')
      .equals('pending')
      .count();
  }

  /**
   * Get count of failed operations
   */
  async getFailedCount(): Promise<number> {
    return await this.pendingOperations
      .where('status')
      .equals('failed')
      .count();
  }
}

// Export singleton instance
export const offlineDb = new GaushalaOfflineDB();

// Export types
export type { Table };
